import {
    AfterViewInit,
    Component,
    DestroyRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import {Community, ProfileQuestion, QuestionAnswer} from "../../_types/communities";
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {LocalStorageService} from "../../_services/local-storage.service";
import {AuthService} from "../../_services/auth.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {CommunitiesService} from "../../_services/communities.service";
import {Router} from "@angular/router";
import {startWith, take} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ThemeService} from "../../_services/theme.service";

@Component({
    selector: 'app-profile-questions',
    templateUrl: './profile-questions.component.html',
    styleUrl: './profile-questions.component.scss'
})
export class ProfileQuestionsComponent implements OnChanges, AfterViewInit {
    @Input() community: Community | undefined | null = this.selectedCommunity;
    @Input() showHeader: boolean = true;
    @Input() card: boolean = true;
    @Input() redirect: boolean = true;
    @Input() isRegistrationPage: boolean = false;

    @Output() storeProfileAnswers: EventEmitter<QuestionAnswer[]> = new EventEmitter<QuestionAnswer[]>();

    public profileForm: FormGroup<{
        [key: string]: FormControl<string | Date | boolean | null>
    }>;
    public dynamicFormFields: ProfileQuestion[] = [];
    public loading: boolean = true;
    public companyImg: string = localStorage.getItem('communityLogo') || 'assets/explainLogoPlaceholder.png';
    public companyName: string = localStorage.getItem('communityName') || 'Explain'
    public today: Date = new Date();

    private isCompanyAlreadyLoaded: boolean = false;

    constructor(
        private readonly fb: FormBuilder,
        private readonly localStorageService: LocalStorageService,
        private readonly authService: AuthService,
        private readonly snackbarService: SnackbarService,
        private readonly communitiesService: CommunitiesService,
        private readonly router: Router,
        private readonly destroyRef: DestroyRef,
        private themeService: ThemeService
    ) {
        this.profileForm = this.fb.group({});
    }

    get selectedCommunity(): Community | null {
        return this.localStorageService.getItem<Community | null>('lastSelectedCommunity', null);
    }

    getControl(id: any): AbstractControl | null {
        return this.profileForm.get(JSON.stringify(id))
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['community']?.currentValue) {
            this.tryReloadCommunityData();
        }
    }

    ngAfterViewInit(): void {
        if (!this.isCompanyAlreadyLoaded) {
            this.tryReloadCommunityData();
        }
    }

    setFieldVisability(id: number, hide: boolean): void {
        const i: number = this.dynamicFormFields.findIndex((x: any): boolean => x.id === id);
        if (i != null && i >= 0) {
            this.dynamicFormFields[i].hidden = hide;
        }
    }

    shuffleArray(array: any) {
        for (let i: number = array.length - 1; i > 0; i--) {
            const j: number = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    }

    randomize(arr: any) {
        // Step 1: Find the indices of objects that have fixed false
        let matchingIndices = arr.reduce((acc: any, obj: any, index: number) => {
            if (obj['random_order']) {
                acc.push(index);
            }
            return acc;
        }, []);

        // Step 2: Extract matching objects based on their indices
        let matchingObjects = matchingIndices.map((index: number) => arr[index]);

        // Step 3: Shuffle the extracted objects
        matchingObjects = this.shuffleArray(matchingObjects);

        // Step 4: Put the shuffled objects back in their original positions
        matchingIndices.forEach((index: number, i: number): void => {
            arr[index] = matchingObjects[i];
        });

        return arr;
    }

    fieldSelect(field: any, option: any): void {
        if (field.selectionChangeInProgress) {
            return; // Prevent recursive calls
        }
        field.selectionChangeInProgress = true;
        try {
            // During multiple choice on value changes check that the option isnt exclusive.
            let mostRecentlySelected
            let formControl: AbstractControl<any, any> | null = this.profileForm.get(field.id.toString());
            let currentValue: any[] = formControl?.value || [];
            if (field.question_type == 'dropdown' && field.dropdown_allows_multiple_selection && option.source.value) {
                mostRecentlySelected = option.source.value;
            } else {
                mostRecentlySelected = option.id;
            }

            const i: number = currentValue?.findIndex((x: any): boolean => x === option.id);
            // if  the id isnt already in the array then pass the value
            if (i < 0) {
                let tempValue = currentValue?.concat(option.id)
                formControl?.setValue(tempValue)
            } else {
                let tempValue = currentValue
                // if the id is already in the array then remove the value
                tempValue.splice(i, 1);
                formControl?.setValue(tempValue)
            }

            for (let fieldOption of field.options) {
                if (((formControl!.value?.includes(fieldOption.id) && field.question_type == 'multiple_choice') || (field.question_type == 'dropdown' && field.dropdown_allows_multiple_selection)) && fieldOption.is_exclusive) {
                    let tempValue = formControl?.value
                    // If option is exclusive remove other options and just set this one

                    if (fieldOption.id == mostRecentlySelected) {
                        // and exclusive has been selected so remove others
                        formControl!.setValue([fieldOption.id]);
                    } else {
                        //  an exclusive pre-exists so must be removed.
                        const i: number = tempValue?.findIndex((x: any): boolean => x === fieldOption.id);

                        tempValue.splice(i, 1);
                        formControl?.setValue(tempValue)
                    }
                }
            }
        } finally {
            field.selectionChangeInProgress = false;
        }
    }

    checkIfRequired(id: number): boolean {
        return this.profileForm.get(JSON.stringify(id))?.hasValidator(Validators.required)!
    }

    checkIfCorrectAge(field: any, value: Date): void {
        if (field.minimum_number_of_years) {
            if (value) {
                const ageDifMs: number = Date.now() - value.getTime();
                const ageDate: Date = new Date(ageDifMs); // miliseconds from epoch
                let years: number = Math.abs(ageDate.getUTCFullYear() - 1970);
                field.incorrectAge = years < field.minimum_number_of_years
            } else {
                field.incorrectAge = true;
            }
        }
    }

    allAgesCorrect(): boolean {
        let allAgesCorrect: boolean = true
        for (let question of this.dynamicFormFields) {
            if (question.incorrectAge) {
                allAgesCorrect = false
            }
        }
        return allAgesCorrect
    }

    formatQuestions(): QuestionAnswer[] {
        let answers: QuestionAnswer[] = [];
        for (let field of this.dynamicFormFields) {
            if (field.question_type) {
                let answer = this.profileForm.get(field.id.toString())!.value;
                if (answer instanceof Date) {
                    answer = answer.toLocaleDateString('en-CA').slice(0, 10);
                }
                if (answer == '') {
                    answer = null;
                }
                switch (field.question_type) {
                    case "single_choice":
                        answers.push({
                            question: field.id,
                            options: [answer]
                        })
                        break;
                    case  "dropdown":
                        if (field.dropdown_allows_multiple_selection) {
                            answers.push({
                                question: field.id,
                                options: answer
                            })
                        } else {
                            answers.push({
                                question: field.id,
                                options: [answer]
                            })
                        }
                        break;
                    case "multiple_choice":
                        answers.push({
                            question: field.id,
                            options: answer
                        })
                        break;
                    case "date":
                        answers.push({
                            question: field.id,
                            answer_date: answer
                        })
                        break;
                    case "text":
                        answers.push({
                            question: field.id,
                            answer_text: answer
                        })
                        break;
                    case "numeric":
                        answers.push({
                            question: field.id,
                            answer_text: answer
                        })
                        break;
                }
            }
        }
        return answers
    }

    submit(): void {
        // first profile submit
        let answers: QuestionAnswer[] = this.formatQuestions();
        this.storeProfileAnswers.emit(answers);
    }

    update(): void {
        this.loading = true;
        // update profile
        let answers: QuestionAnswer[] = this.formatQuestions();
        let that = this
        this.sendProfileQuestions(this.authService.getUser()?.id, answers).then(function (): void {
            that.reCheckUserFinishRegistration();
        }).catch(function (): void {
            that.loading = false;
        })
    }

    reCheckUserFinishRegistration(): void {
        this.communitiesService.getUserInformationForCommunity(this.community!.id).subscribe(res => {
            this.communitiesService.profileRefreshed = !res ? true : (res?.profile_refreshed === null || !!res?.profile_refreshed);
            if (this.communitiesService.profileRefreshed || !this.community?.questions?.length) {
                if (this.isRegistrationPage) {
                    this.snackbarService.openSuccessSnackBar('You successfully finished onboarding');

                } else {
                    this.snackbarService.openSuccessSnackBar('You successfully updated your profile');
                }
                if (this.redirect) {
                    this.router.navigate(['/feed']);
                }
                this.loading = false;
                return;
            } else {
                this.loading = false;
                this.snackbarService.openErrorSnackBar('Onboarding in selected community not finished');
                window.location.reload();
            }
        }, e => {
            this.loading = false;
            this.snackbarService.openErrorSnackBar(e);
            window.location.reload();
        })
    }

    sendProfileQuestions(userId: number, answers: QuestionAnswer[]) {
        return new Promise((resolve, reject): void => {
            let done: number = 0;
            for (let answer of answers) {
                (answer as any)['user'] = userId;
                if (answer.options && answer.options[0] == null) {
                    answer.options = [];
                }
                this.authService.postProfileQuestionAnswer(answer).pipe(take(1)).subscribe(r => {
                    done++
                    if (done == answers.length) {
                        resolve(r)
                    }
                }, e => {
                    reject(e)
                    this.snackbarService.openErrorSnackBar(e);
                })
            }
        });
    }

    private tryReloadCommunityData(): void {
        if (this.community?.id) {
            this.loading = true;
            this.isCompanyAlreadyLoaded = true; // This flag only for skip initial loading(for separate page logic)
            this.companyImg = this.community.logo;
            this.companyName = this.community.name;
            if (this.community?.main_color_hex) {
                this.themeService.savePrimaryColor(this.community?.main_color_hex)
            }
            if (this.community?.accent_color_hex) {
                this.themeService.saveAccentColor(this.community?.accent_color_hex)
            }
            this.authService.getCommunityProfileQuestions(this.community?.id).pipe(take(1)).subscribe(res => {
                this.dynamicFormFields = res;
                this.dynamicFormFields?.sort((a, b) => a.order - b.order);
                this.randomize(this.dynamicFormFields)
                let parentQuestions: {
                    id: number,
                    children: [{ id: number, option: number, required?: boolean }]
                }[] = [];
                for (let response of this.dynamicFormFields) {


                    if (response.question_type) {
                        let control = new FormControl('');
                        if (response.required) {
                            control.addValidators(Validators.required);
                        }
                        if (response.options && response.options.length > 0) {
                            this.randomize(response.options)

                        }

                        if (response.parent_question || response.parent_option) {
                            control = new FormControl('');

                            const i: number = parentQuestions.findIndex((x: any): boolean => x.id === response.parent_question);
                            if (i != null && i >= 0) {
                                parentQuestions[i].children.push({
                                    id: response.id,
                                    option: response.parent_option,
                                    required: response.required
                                })
                            } else {
                                parentQuestions.push({
                                    id: response.parent_question,
                                    children: [{
                                        id: response.id,
                                        option: response.parent_option,
                                        required: response.required
                                    }]
                                })
                            }
                        }
                        this.profileForm.addControl(`${response.id}`, control);

                        if (response.minimum_number_of_years) {
                            response.incorrectAge = true;
                        }
                    }
                }

                for (let parentQuestion of parentQuestions) {
                    // When parent question changes
                    this.profileForm.get(JSON.stringify(parentQuestion.id))?.valueChanges
                        .pipe(startWith(''), takeUntilDestroyed(this.destroyRef))
                        .subscribe((res): void => {
                            for (let child of parentQuestion.children) {
                                if (res && !child.option) {
                                    //     No parent option and question has been answered so display child

                                    this.setFieldVisability(child.id, false);
                                    if (child.required) {
                                        this.profileForm.get(JSON.stringify(child.id))?.addValidators(Validators.required);
                                    }
                                    this.profileForm.get(JSON.stringify(child.id))?.updateValueAndValidity();
                                } else if (child.option && ((Array.isArray(res) && res.includes(child.option)) || res == child.option)) {
                                    //     Parent option and parent question option matches then display child
                                    this.setFieldVisability(child.id, false);
                                    if (child.required) {
                                        this.profileForm.get(JSON.stringify(child.id))?.addValidators(Validators.required);
                                    }
                                    this.profileForm.get(JSON.stringify(child.id))?.updateValueAndValidity();
                                } else {
                                    // Hide child
                                    this.setFieldVisability(child.id, true);
                                    this.profileForm.get(JSON.stringify(child.id))?.setValue(null)
                                    this.profileForm.get(JSON.stringify(child.id))?.clearValidators();
                                    this.profileForm.get(JSON.stringify(child.id))?.updateValueAndValidity();
                                }
                            }
                        });
                }

                // Commented out as client does not want questions pre-filled in
                // if (!this.isRegistrationPage && this.authService.getUser() && this.community?.id) {
                //     this.authService.getProfileQuestionAnswer(this.community?.id).subscribe(r => {
                //         for (let answer of r.results) {
                //             let value = answer.answer_date || answer.answer_text || answer.options
                //             if (Array.isArray(value)) {
                //                 // if single choice field remove from array
                //                 const i: number = this.dynamicFormFields.findIndex((x: any): boolean => x.id === answer.question);
                //                 if (this.dynamicFormFields[i].question_type !== 'multiple_choice' && (this.dynamicFormFields[i].question_type !== 'dropdown' || !this.dynamicFormFields[i].dropdown_allows_multiple_selection)) {
                //                     value = value[0]
                //                 }
                //             } else if (answer.answer_date) {
                //                 value = new Date(value)
                //             }
                //             this.profileForm.get(JSON.stringify(answer.question))?.setValue(value)
                //             this.profileForm.get(JSON.stringify(answer.question))?.updateValueAndValidity();
                //
                //             if (answer.answer_date) {
                //                 // if answer date with minimum years then trigger validation check
                //                 const i: number = this.dynamicFormFields.findIndex((x: any): boolean => x.id === answer.question);
                //                 if (this.dynamicFormFields[i].minimum_number_of_years) {
                //                     this.checkIfCorrectAge(this.dynamicFormFields[i], value)
                //                 }
                //             }
                //         }
                //     }, e => {
                //         console.error(e)
                //     })
                // }
                this.loading = false;
            }, e => {
                this.snackbarService.openErrorSnackBar('Error receiving community onboarding questions please try again later');
            })
        } else {
            this.loading = false;
        }
    }
}
